import React, { useState } from 'react';
import { ChevronDownIcon } from '../../icons';

interface ComponentCardProps {
  title: string;
  children: React.ReactNode;
  className?: string;
  /** Plain description shown under the title */
  description?: string;
  /** Save button label (requires onSave) */
  desc?: string;
  onSave?: () => void;
  isSaveDisabled?: boolean;
  collapsible?: boolean;
  defaultExpanded?: boolean;
}

const ComponentCard: React.FC<ComponentCardProps> = ({
  title,
  children,
  className = '',
  description = '',
  desc = '',
  onSave,
  isSaveDisabled = false,
  collapsible = false,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isExpanded = !collapsible || expanded;
  const resolvedDescription = description || (!onSave && desc ? desc : '');

  const toggleExpanded = () => {
    if (collapsible) {
      setExpanded((prev) => !prev);
    }
  };

  return (
    <div className={`app-panel w-full rounded-[28px] ${className}`}>
      <div
        className={`px-5 py-4 md:px-6 ${
          collapsible ? 'cursor-pointer select-none' : ''
        }`}
        onClick={collapsible ? toggleExpanded : undefined}
        onKeyDown={
          collapsible
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggleExpanded();
                }
              }
            : undefined
        }
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? isExpanded : undefined}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-[rgb(var(--app-text))]">
              {title}
            </h3>
            {resolvedDescription && (
              <p className="mt-1.5 text-sm leading-relaxed text-[rgb(var(--app-muted))]">
                {resolvedDescription}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {onSave && desc && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onSave();
                }}
                disabled={isSaveDisabled}
                className={`mt-0.5 inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium shadow-sm ${
                  isSaveDisabled
                    ? 'cursor-not-allowed bg-gray-200'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="mr-2 h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z" />
                </svg>
                {desc}
              </button>
            )}
            {collapsible && (
              <ChevronDownIcon
                className={`mt-0.5 h-5 w-5 shrink-0 text-[rgb(var(--app-muted))] transition-transform ${
                  isExpanded ? 'rotate-180' : ''
                }`}
              />
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="overflow-visible border-t border-[rgb(var(--app-border)/0.62)] p-4 sm:p-6">
          <div className="space-y-6">{children}</div>
        </div>
      )}
    </div>
  );
};

export default ComponentCard;
